import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { familyOf } from './NavPlan';
import { PARAM_LEVELS, PARAM_LABELS } from '@/hooks/useUrlState';

const HOME = { admin: '/admin/dashboard', employee: '/employee/dashboard', comptable: '/comptable' };
const nice = (s) => (s || '').replace(/[-_]/g, ' ').replace(/^\w/, c => c.toUpperCase());
const DETAIL_PARENTS = { '/admin/employee/': '/admin/employees', '/admin/store/': '/admin/stores', '/admin/enseigne/': '/admin/enseignes' };

/** Niveaux de l'écran courant : [{label, to}] du plus général au plus précis (hors rubrique). */
export function levelsOf(pathname, search, items = []) {
  const sp = new URLSearchParams(search);
  const levels = [];
  const detailParent = Object.keys(DETAIL_PARENTS).find(p => pathname.startsWith(p));
  const basePath = detailParent ? DETAIL_PARENTS[detailParent] : pathname.split('/').slice(0, 3).join('/');
  const item = items.find(i => basePath === i.path || basePath.startsWith(i.path + '/'));
  levels.push({ label: item?.label || nice(basePath.split('/').pop()), to: basePath });
  if (detailParent) levels.push({ label: 'Fiche', to: pathname });
  else if (pathname.split('/').length > 3) levels.push({ label: nice(pathname.split('/')[3]), to: pathname.split('/').slice(0, 4).join('/') });
  const acc = new URLSearchParams();
  const present = PARAM_LEVELS.filter(k => sp.has(k));
  const others = [...sp.keys()].filter(k => !PARAM_LEVELS.includes(k));
  others.forEach(k => acc.set(k, sp.get(k)));
  present.forEach(k => {
    acc.set(k, sp.get(k));
    const prefix = PARAM_LABELS[k];
    levels.push({ label: prefix ? `${prefix} ${sp.get(k)}`.trim() : nice(sp.get(k)), to: `${pathname}?${acc}` });
  });
  return levels;
}

export function parentOf(pathname, search, items, role) {
  const levels = levelsOf(pathname, search, items);
  const home = HOME[pathname.startsWith('/employee') ? 'employee' : pathname.startsWith('/comptable') ? 'comptable' : 'admin'];
  if (levels.length >= 2) return levels[levels.length - 2].to;
  const fam = familyOf(pathname);
  if (fam && fam.key !== 'accueil' && fam.paths[0] !== pathname && items.some(i => i.path === fam.paths[0])) return null;
  return pathname === home ? null : home;
}

/** Bouton « ← Retour » (niveau parent immédiat) + fil d'Ariane cliquable (desktop) / court (mobile). */
export const BackBar = ({ items = [], role }) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const fam = familyOf(pathname);
  const levels = levelsOf(pathname, search, items);
  const parent = parentOf(pathname, search, items, role);
  const depth = levels.length;
  if (!parent && !fam) return null;
  const go = (e) => {
    e.preventDefault();
    if (window.__lgDirty && !window.confirm('Des modifications ne sont pas enregistrées. Quitter cet écran ?')) return;
    window.__lgDirty = false;
    navigate(parent);
  };
  return (
    <div className="flex items-center gap-2 mb-3 min-w-0" data-testid="back-bar">
      {parent && depth >= 2 && (
        <Link to={parent} onClick={go} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 min-h-[44px] sm:min-h-[36px] text-sm font-semibold text-slate-700 hover:border-[#003480]/40 hover:text-[#003480] shrink-0" data-testid="back-button" aria-label="Retour au niveau précédent">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
      )}
      <nav className="hidden sm:flex items-center gap-1 text-xs text-slate-500 min-w-0" aria-label="Fil d'Ariane" data-testid="breadcrumb">
        {fam && <Link to={fam.paths.find(p => items.some(i => i.path === p)) || levels[0].to} className="font-semibold hover:underline shrink-0" style={{ color: fam.color }} data-testid="crumb-family">{fam.label}</Link>}
        {levels.map((l, i) => (
          <span key={l.to} className="flex items-center gap-1 min-w-0">
            {(fam || i > 0) && <ChevronRight className="w-3 h-3 shrink-0" />}
            {i === levels.length - 1 ? <span className="truncate text-slate-800 font-medium" data-testid={`crumb-${i}`}>{l.label}</span>
              : <Link to={l.to} className="truncate hover:underline" data-testid={`crumb-${i}`}>{l.label}</Link>}
          </span>
        ))}
      </nav>
      <span className="sm:hidden text-xs text-slate-500 truncate" data-testid="breadcrumb-mobile">{depth >= 2 ? levels[depth - 2].label : fam?.label}</span>
    </div>
  );
};

/**
 * Historique ↔ fenêtres : une fenêtre ouverte pousse une entrée ; « retour » la ferme (Échap) au lieu de quitter la page.
 * Formulaires : toute saisie marque l'écran « modifié » (window.__lgDirty) ; retour → confirmation ; soumission / navigation réinitialise.
 */
export function useHistoryGuards() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => { window.__lgDirty = false; }, [pathname, search]);
  useEffect(() => {
    const onInput = (e) => { const t = e.target; if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT') && t.type !== 'search' && !t.closest('[data-no-dirty]') && !(t.getAttribute('placeholder') || '').toLowerCase().startsWith('recherch')) window.__lgDirty = true; };
    const onSubmit = () => { window.__lgDirty = false; };
    const onClick = (e) => { const b = e.target.closest('button[type=submit], [data-testid$="-save"], [data-testid$="-submit"], [data-testid$="-submit-button"]'); if (b) window.__lgDirty = false; };
    const onBeforeUnload = (e) => { if (window.__lgDirty) { e.preventDefault(); e.returnValue = ''; } };
    document.addEventListener('input', onInput, true); document.addEventListener('submit', onSubmit, true); document.addEventListener('click', onClick, true); window.addEventListener('beforeunload', onBeforeUnload);
    return () => { document.removeEventListener('input', onInput, true); document.removeEventListener('submit', onSubmit, true); document.removeEventListener('click', onClick, true); window.removeEventListener('beforeunload', onBeforeUnload); };
  }, []);
  useEffect(() => {
    let openCount = 0;
    const dialogs = () => document.querySelectorAll('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');
    const obs = new MutationObserver(() => {
      const n = dialogs().length;
      if (n > openCount) { window.history.pushState({ ...(window.history.state || {}), lgDialog: n }, ''); }
      else if (n < openCount && window.history.state?.lgDialog) { window.__lgPopSelf = true; window.history.back(); }
      openCount = n;
    });
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-state'] });
    const onPop = () => {
      if (window.__lgPopSelf) { window.__lgPopSelf = false; return; }
      const n = dialogs().length;
      if (n > 0) {
        if (window.__lgDirty && !window.confirm('Des modifications ne sont pas enregistrées. Fermer quand même ?')) { window.history.pushState({ lgDialog: n }, ''); return; }
        window.__lgDirty = false; openCount = Math.max(0, n - 1);
        document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
        return;
      }
      if (window.__lgDirty && !window.confirm('Des modifications ne sont pas enregistrées. Quitter cet écran ?')) { navigate(1); return; }
      window.__lgDirty = false;
    };
    window.addEventListener('popstate', onPop);
    return () => { obs.disconnect(); window.removeEventListener('popstate', onPop); };
  }, [navigate]);
}

export const HistoryGuards = () => { useHistoryGuards(); return null; };
